import Seo, { breadcrumbSchema, faqSchema } from "@/components/Seo";
import Crumbs from "@/components/Crumbs";
import HelpCta from "@/components/HelpCta";
import { FAQ } from "@/data/faq";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { Link } from "react-router-dom";
import { Phone, ExternalLink } from "lucide-react";

export default function PreguntasFrecuentes() {
  const crumbs = [{ name: "Preguntas Frecuentes", path: "/preguntas-frecuentes" }];
  const schemas = [
    faqSchema(FAQ),
    breadcrumbSchema([{ name: "Inicio", path: "/" }, ...crumbs]),
  ];

  return (
    <>
      <Seo
        title="Preguntas Frecuentes sobre la Pasta Base | pasta-base.cl"
        description="Respuestas a las preguntas más frecuentes sobre la pasta base: qué es, efectos, tratamiento, costos, cómo ayudar a un familiar y dónde encontrar ayuda en Chile."
        path="/preguntas-frecuentes"
        schema={schemas}
      />

      <div className="mx-auto max-w-4xl px-5 py-16 lg:px-8">
        <Crumbs items={crumbs} />
        <header className="max-w-3xl">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400">
            20 preguntas · respuestas completas
          </span>
          <h1 className="mt-3 text-4xl font-extrabold tracking-tight text-slate-50 sm:text-5xl">
            Preguntas Frecuentes sobre la Pasta Base
          </h1>
          <p className="mt-4 text-lg leading-relaxed text-slate-300">
            Lo que más se pregunta en Chile sobre la pasta base: composición,
            efectos, tratamiento, costos, recaídas y cómo ayudar a un ser
            querido. Respuestas claras, basadas en evidencia.
          </p>
        </header>

        <div className="mt-12 overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/40">
          <Accordion type="single" collapsible className="divide-y divide-slate-800">
            {FAQ.map((f, i) => (
              <AccordionItem
                key={f.id}
                value={f.id}
                data-testid={`faq-${f.id}`}
                className="border-0 px-6"
              >
                <AccordionTrigger className="py-5 text-left text-base font-semibold text-slate-100 hover:text-emerald-300 hover:no-underline">
                  <span className="flex items-start gap-3">
                    <span className="mt-0.5 text-xs font-bold text-emerald-400">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span>{f.question}</span>
                  </span>
                </AccordionTrigger>
                <AccordionContent className="pb-6 text-sm leading-relaxed text-slate-300">
                  <div className="pl-8">{f.answer}</div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* Resources quick access (related to Q20) */}
        <section className="mt-12 rounded-3xl border border-red-500/20 bg-gradient-to-br from-red-500/5 to-slate-900 p-8">
          <h2 className="text-2xl font-bold text-slate-50">¿Dónde buscar ayuda ahora mismo?</h2>
          <p className="mt-2 text-sm text-slate-300">
            Si necesitas ayuda hoy, estos son los recursos inmediatos en Chile:
          </p>
          <div className="mt-6 grid gap-3 md:grid-cols-3">
            <a
              href="tel:1412"
              className="group flex items-start gap-3 rounded-xl border border-red-500/30 bg-red-500/10 p-4 transition hover:bg-red-500/20"
            >
              <Phone className="mt-0.5 h-5 w-5 shrink-0 text-red-300" />
              <div>
                <div className="text-sm font-bold text-slate-50">
                  Fono Drogas SENDA: 1412
                </div>
                <p className="mt-0.5 text-xs text-slate-400">24/7 · gratuito · confidencial</p>
              </div>
            </a>
            <a
              href="https://sinadicciones.org/centros-de-rehabilitacion-drogas-chile"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-start gap-3 rounded-xl border border-slate-700 bg-slate-900/80 p-4 transition hover:bg-slate-900"
            >
              <ExternalLink className="mt-0.5 h-5 w-5 shrink-0 text-emerald-300" />
              <div>
                <div className="text-sm font-bold text-slate-50">Centros de Rehabilitación</div>
                <p className="mt-0.5 text-xs text-slate-400">+88 centros verificados · 16 regiones</p>
              </div>
            </a>
            <a
              href="https://sinadicciones.org/profesionales"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-start gap-3 rounded-xl border border-slate-700 bg-slate-900/80 p-4 transition hover:bg-slate-900"
            >
              <ExternalLink className="mt-0.5 h-5 w-5 shrink-0 text-cyan-300" />
              <div>
                <div className="text-sm font-bold text-slate-50">Profesionales especializados</div>
                <p className="mt-0.5 text-xs text-slate-400">+55 psicólogos y psiquiatras</p>
              </div>
            </a>
          </div>
          <div className="mt-5">
            <Link
              to="/test-dependencia"
              className="text-sm font-semibold text-emerald-400 hover:text-emerald-300"
            >
              ¿Aún tienes dudas? Haz el test de dependencia →
            </Link>
          </div>
        </section>
      </div>

      <section className="border-t border-slate-800 bg-slate-950">
        <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
          <HelpCta testidPrefix="faq" />
        </div>
      </section>
    </>
  );
}
